// frontend/src/components/PermissionRoute.js
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import usePermission from '../hooks/usePermission';

/**
 * A route guard component that checks if the user has the required permission(s)
 * If they do, it renders the children components, otherwise redirects to a fallback route
 *
 * @param {string} application - The application name to check permissions for
 * @param {string|string[]} permission - The required permission(s). Can be a string or array of strings
 * @param {boolean} requireAll - If permission is an array, whether to require ALL permissions (true) or ANY permission (false). Default: false (OR logic)
 * @param {string} fallbackPath - Where to redirect if permission check fails
 * @returns {JSX.Element} The protected route
 */
const PermissionRoute = ({ application, permission, requireAll = false, fallbackPath = '/dashboard' }) => {
  const { hasPermission } = usePermission();

  // Check if the user has the required permission(s)
  let isAllowed = false;

  if (Array.isArray(permission)) {
    // Multiple permissions
    if (requireAll) {
      // AND logic - require ALL permissions
      isAllowed = permission.every(perm => hasPermission(application, perm));
    } else {
      // OR logic - require ANY permission (default)
      isAllowed = permission.some(perm => hasPermission(application, perm));
    }
  } else {
    // Single permission
    isAllowed = hasPermission(application, permission);
  }

  // If allowed, render the child routes, otherwise redirect
  return isAllowed ? <Outlet /> : <Navigate to={fallbackPath} />;
};

export default PermissionRoute;
